import React, { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Minus, Plus, Maximize2 } from "lucide-react";

const LENS = 300;
const DOC_STEPS = [1, 1.25, 1.5, 2, 2.5, 3, 4];
const readDocZoom = () => {
  const v = Number(localStorage.getItem("inbox.docZoom"));
  return DOC_STEPS.includes(v) ? v : 1;
};

/** Bild mit Hover-Lupe (Mausrad = Lupenstufe) und Dokument-Zoom (+/−, bleibt beim Durchblättern erhalten). */
export function MagnifierImage({ src, hiresSrc, alt, className = "", testid = "magnifier-image" }) {
  const imgRef = useRef(null);
  const boxRef = useRef(null);
  const [enabled, setEnabled] = useState(() => localStorage.getItem("inbox.lupe") !== "off");
  const [zoom, setZoom] = useState(() => Number(localStorage.getItem("inbox.lupe.zoom")) || 2.5);
  const [docZoom, setDocZoom] = useState(readDocZoom);
  const [lens, setLens] = useState(null);
  const [boxH, setBoxH] = useState(0);

  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setBoxH(el.clientHeight));
    ro.observe(el);
    setBoxH(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  useEffect(() => { localStorage.setItem("inbox.lupe", enabled ? "on" : "off"); }, [enabled]);
  useEffect(() => { localStorage.setItem("inbox.lupe.zoom", String(zoom)); }, [zoom]);
  useEffect(() => { localStorage.setItem("inbox.docZoom", String(docZoom)); }, [docZoom]);

  const stepDoc = (dir) => setDocZoom((z) => {
    const i = DOC_STEPS.indexOf(z);
    return DOC_STEPS[Math.min(DOC_STEPS.length - 1, Math.max(0, (i < 0 ? 0 : i) + dir))];
  });

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) { e.preventDefault(); stepDoc(e.deltaY < 0 ? 1 : -1); return; }
      if (!enabled) return;
      e.preventDefault();
      setZoom((z) => Math.min(5, Math.max(1.5, +(z + (e.deltaY < 0 ? 0.5 : -0.5)).toFixed(1))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [enabled]);

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.key === "+" || e.key === "=") { e.preventDefault(); stepDoc(1); }
      else if (e.key === "-") { e.preventDefault(); stepDoc(-1); }
      else if (e.key === "0") { e.preventDefault(); setDocZoom(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onMove = (e) => {
    const img = imgRef.current;
    if (!enabled || !img || !img.naturalWidth) return;
    const r = img.getBoundingClientRect();
    const scale = Math.min(r.width / img.naturalWidth, r.height / img.naturalHeight);
    const cw = img.naturalWidth * scale, ch = img.naturalHeight * scale;
    const ox = r.left + (r.width - cw) / 2, oy = r.top + (r.height - ch) / 2;
    const px = (e.clientX - ox) / cw, py = (e.clientY - oy) / ch;
    if (px < 0 || px > 1 || py < 0 || py > 1) { setLens(null); return; }
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = e.clientX + 28;
    if (left + LENS > vw - 8) left = e.clientX - LENS - 28;
    const top = Math.min(Math.max(8, e.clientY - LENS / 2), vh - LENS - 8);
    setLens({ left, top, bgSize: `${cw * zoom}px ${ch * zoom}px`, bgPos: `${-(px * cw * zoom - LENS / 2)}px ${-(py * ch * zoom - LENS / 2)}px` });
  };

  const zoomed = docZoom > 1;
  return (
    <div ref={boxRef} className={`relative flex-1 min-h-0 w-full ${zoomed ? "overflow-auto" : "flex items-center justify-center"} ${className}`} onMouseLeave={() => setLens(null)} data-testid="magnifier-box">
      <div className={zoomed ? "min-w-full min-h-full flex items-start justify-center p-2" : "contents"}>
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onMouseMove={onMove}
          style={zoomed && boxH ? { height: `${Math.round((boxH - 16) * docZoom)}px`, maxWidth: "none", maxHeight: "none", width: "auto" } : undefined}
          className={`${zoomed ? "" : "max-h-full max-w-full"} object-contain rounded-lg shadow-lg ${enabled ? "cursor-zoom-in" : ""}`}
          data-testid={testid}
          data-doc-zoom={docZoom}
        />
      </div>
      <div className="absolute top-2 left-2 inline-flex items-center rounded-full border border-slate-600 bg-slate-800/85 text-slate-100 text-[11px] backdrop-blur overflow-hidden" data-testid="doc-zoom-control" title="Dokument vergrössern: + / − (Tasten), Ctrl+Mausrad · bleibt beim Durchblättern erhalten">
        <button type="button" onClick={() => stepDoc(-1)} disabled={docZoom === DOC_STEPS[0]} className="px-2 py-1 hover:bg-slate-700 disabled:opacity-40" data-testid="doc-zoom-out" aria-label="Verkleinern"><Minus className="h-3.5 w-3.5" /></button>
        <span className="px-1.5 font-mono tabular-nums" data-testid="doc-zoom-value">{Math.round(docZoom * 100)} %</span>
        <button type="button" onClick={() => stepDoc(1)} disabled={docZoom === DOC_STEPS[DOC_STEPS.length - 1]} className="px-2 py-1 hover:bg-slate-700 disabled:opacity-40" data-testid="doc-zoom-in" aria-label="Vergrössern"><Plus className="h-3.5 w-3.5" /></button>
        {zoomed && (
          <button type="button" onClick={() => setDocZoom(1)} className="px-2 py-1 border-l border-slate-600 hover:bg-slate-700 inline-flex items-center gap-1" data-testid="doc-zoom-fit" title="Einpassen (Taste 0)"><Maximize2 className="h-3 w-3" /> Einpassen</button>
        )}
      </div>
      <button
        type="button"
        onClick={() => { setEnabled((v) => !v); setLens(null); }}
        className={`absolute top-2 right-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] border backdrop-blur transition-colors ${enabled ? "bg-emerald-500/90 border-emerald-400 text-white" : "bg-slate-800/80 border-slate-600 text-slate-200 hover:bg-slate-700"}`}
        title={enabled ? "Lupe ausschalten (Mausrad = Lupenstufe)" : "Lupe einschalten – Maus auf den Beleg zeigt den Ausschnitt vergrössert"}
        data-testid="magnifier-toggle"
      >
        {enabled ? <ZoomIn className="h-3.5 w-3.5" /> : <ZoomOut className="h-3.5 w-3.5" />}
        Lupe {enabled ? `${zoom}×` : "aus"}
      </button>
      {lens && enabled && (
        <div
          className="pointer-events-none fixed z-[200] rounded-2xl border-2 border-emerald-400 shadow-2xl bg-white bg-no-repeat"
          style={{ left: lens.left, top: lens.top, width: LENS, height: LENS, backgroundImage: `url(${hiresSrc || src})`, backgroundSize: lens.bgSize, backgroundPosition: lens.bgPos }}
          data-testid="magnifier-lens"
        >
          <span className="absolute bottom-1 right-2 text-[10px] font-mono text-emerald-700 bg-white/80 rounded px-1">{zoom}× · Mausrad</span>
        </div>
      )}
    </div>
  );
}
